'use client'

import {
  Badge,
  Button,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Slider,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui'
import { Link } from '@tanstack/react-router'
import {
  ChevronDown,
  ExternalLink,
  Info,
  Plus,
  Tag,
  Trash2,
} from 'lucide-react'
import { useEffect, useRef } from 'react'

import type { AgentOperationalPolicy } from '@/types/agents'
import type { CustomFieldDef, KanbanStage } from '@/types/leads'

interface QualificationTabProps {
  customFields: CustomFieldDef[]
  stages: KanbanStage[]
  policy: AgentOperationalPolicy
  onPolicyChange: (policy: AgentOperationalPolicy) => void
}

type QualificationCondition =
  AgentOperationalPolicy['qualification']['groups'][number]['conditions'][number]

const OPERATORS: Array<{
  value: QualificationCondition['operator']
  label: string
}> = [
  { value: 'EXISTS', label: 'Está preenchido' },
  { value: 'NOT_EXISTS', label: 'Não está preenchido' },
  { value: 'EQUALS', label: 'É igual a' },
  { value: 'NOT_EQUALS', label: 'É diferente de' },
  { value: 'CONTAINS', label: 'Contém' },
  { value: 'GT', label: 'Maior que' },
  { value: 'GTE', label: 'Maior ou igual' },
  { value: 'LT', label: 'Menor que' },
  { value: 'LTE', label: 'Menor ou igual' },
]

const DEFAULT_TOOL_IDS: AgentOperationalPolicy['tools'][number]['toolId'][] = [
  'lead.save_field',
  'lead.add_note',
  'lead.add_tag',
  'schedule.check_availability',
  'schedule.book',
  'schedule.reschedule',
  'schedule.cancel',
  'cadence.enroll',
  'attendance.transfer_to_human',
]

const SYSTEM_FIELDS: CustomFieldDef[] = [
  {
    id: 'system:name',
    key: 'name',
    name: 'Nome',
    fieldType: 'TEXT',
  },
  {
    id: 'system:email',
    key: 'email',
    name: 'E-mail',
    fieldType: 'EMAIL',
  },
  {
    id: 'system:phone',
    key: 'phone',
    name: 'Telefone / WhatsApp',
    fieldType: 'PHONE',
  },
  {
    id: 'system:company',
    key: 'company',
    name: 'Empresa',
    fieldType: 'TEXT',
  },
  {
    id: 'system:city',
    key: 'cidade',
    name: 'Cidade',
    fieldType: 'TEXT',
  },
]

function parseFieldOptions(options?: string | null) {
  return (options ?? '')
    .split(/\r?\n|,/)
    .map((option) => option.trim())
    .filter(Boolean)
}

function operatorsForField(field?: CustomFieldDef) {
  const type = field?.fieldType?.toUpperCase()
  const allowed =
    type === 'NUMBER' || type === 'DATE'
      ? [
          'EXISTS',
          'NOT_EXISTS',
          'EQUALS',
          'NOT_EQUALS',
          'GT',
          'GTE',
          'LT',
          'LTE',
        ]
      : type === 'SELECT' || type === 'BOOLEAN'
        ? ['EXISTS', 'NOT_EXISTS', 'EQUALS', 'NOT_EQUALS']
        : ['EXISTS', 'NOT_EXISTS', 'EQUALS', 'NOT_EQUALS', 'CONTAINS']

  return OPERATORS.filter((operator) => allowed.includes(operator.value))
}

function defaultOperatorForField(
  field?: CustomFieldDef
): QualificationCondition['operator'] {
  const type = field?.fieldType?.toUpperCase()
  return type === 'SELECT' || type === 'BOOLEAN' ? 'EQUALS' : 'EXISTS'
}

export function createDefaultOperationalPolicy(
  requiredFieldIds: string[] = []
): AgentOperationalPolicy {
  return {
    schemaVersion: 1,
    tools: DEFAULT_TOOL_IDS.map((toolId) => ({
      toolId,
      enabled: true,
      confirmation: toolId.startsWith('schedule.') ? 'CONTACT' : 'NONE',
    })),
    qualification: {
      fields: requiredFieldIds.map((fieldId, order) => ({
        fieldId,
        requirement: 'REQUIRED',
        order,
        suggestedQuestion: null,
        allowInference: false,
        minConfidence: 1,
        requireConfirmation: false,
        allowCorrection: true,
        sensitive: false,
      })),
      groups: [],
      disqualifiers: [],
      qualifiedScoreThreshold: 75,
      missingDataOutcome: 'IN_PROGRESS',
    },
    funnelTransitions: [],
  }
}

type FunnelEvent = AgentOperationalPolicy['funnelTransitions'][number]['event']

// Eventos do funil expostos na UI, com a heurística de nome usada para sugerir
// automaticamente um estágio do kanban quando o agente ainda não tem funil.
const FUNNEL_EVENTS: Array<{
  event: FunnelEvent
  label: string
  help: string
  match: RegExp
  primary: boolean
}> = [
  {
    event: 'QUALIFIED',
    label: 'Qualificado',
    help: 'Lead atingiu o score e os critérios.',
    match: /qualific/,
    primary: true,
  },
  {
    event: 'UNQUALIFIED',
    label: 'Desqualificado / Fora de ICP',
    help: 'Bateu um desqualificador ou ficou fora do perfil.',
    match: /fora de icp|\bicp\b|desqualific|perdid|descart/,
    primary: true,
  },
  {
    event: 'APPOINTMENT_BOOKED',
    label: 'Reunião agendada',
    help: 'O lead agendou um horário.',
    match: /reuni|agend|demonstr|\bcall\b/,
    primary: false,
  },
  {
    event: 'NEEDS_HUMAN',
    label: 'Precisa de humano',
    help: 'Encaminhado para um atendente humano.',
    match: /humano|atend|vendedor|especialista/,
    primary: false,
  },
  {
    event: 'TRANSFERRED',
    label: 'Transferido',
    help: 'Transferido para outro time.',
    match: /transfer/,
    primary: false,
  },
]

function normalizeStageName(name: string) {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

// Sugere um estágio por evento casando pelo nome do kanban (sem acento/caixa).
// QUALIFIED evita casar "desqualificado". Só retorna os que tiverem match confiante.
export function suggestFunnelTransitions(
  stages: KanbanStage[]
): AgentOperationalPolicy['funnelTransitions'] {
  const transitions: AgentOperationalPolicy['funnelTransitions'] = []
  for (const { event, match } of FUNNEL_EVENTS) {
    const stage = stages.find((item) => {
      const name = normalizeStageName(item.name)
      if (event === 'QUALIFIED') {
        return /qualific/.test(name) && !/desqualific/.test(name)
      }
      return match.test(name)
    })
    if (stage) transitions.push({ event, stageId: stage.id })
  }
  return transitions
}

function ConditionsEditor({
  title,
  description,
  customFields,
  conditions,
  showWeight,
  onChange,
}: {
  title: string
  description: string
  customFields: CustomFieldDef[]
  conditions: QualificationCondition[]
  showWeight: boolean
  onChange: (conditions: QualificationCondition[]) => void
}) {
  function update(index: number, patch: Partial<QualificationCondition>) {
    onChange(
      conditions.map((condition, currentIndex) =>
        currentIndex === index ? { ...condition, ...patch } : condition
      )
    )
  }

  return (
    <div className="border-border bg-card rounded-lg border p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold">{title}</p>
          <p className="text-muted-foreground mt-1 text-xs">{description}</p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={customFields.length === 0}
          onClick={() =>
            onChange([
              ...conditions,
              {
                fieldId: customFields[0]?.id ?? '',
                operator: defaultOperatorForField(customFields[0]),
                value: null,
                weight: showWeight ? 10 : 0,
              },
            ])
          }
        >
          <Plus className="size-3.5" />
          Adicionar
        </Button>
      </div>

      {conditions.length === 0 ? (
        <p className="text-muted-foreground mt-4 rounded-md border border-dashed p-4 text-center text-xs">
          Nenhuma condição configurada.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-37.5">Campo</TableHead>
                <TableHead className="min-w-37.5">Operador</TableHead>
                <TableHead className="min-w-37.5">Valor</TableHead>
                {showWeight && <TableHead className="w-24">Pontos</TableHead>}
                <TableHead className="w-10">
                  <span className="sr-only">Ações</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {conditions.map((condition, index) => {
                const selectedField = customFields.find(
                  (field) => field.id === condition.fieldId
                )
                const fieldType = selectedField?.fieldType?.toUpperCase()
                const fieldOptions = parseFieldOptions(selectedField?.options)
                const availableOperators = operatorsForField(selectedField)
                const needsValue = !['EXISTS', 'NOT_EXISTS'].includes(
                  condition.operator
                )
                return (
                  <TableRow key={`${condition.fieldId}-${index}`}>
                    <TableCell className="align-top">
                      <Select
                        value={condition.fieldId}
                        onValueChange={(fieldId) => {
                          const field = customFields.find(
                            (item) => item.id === fieldId
                          )
                          const operator = defaultOperatorForField(field)
                          update(index, { fieldId, operator, value: null })
                        }}
                      >
                        <SelectTrigger className="min-w-0">
                          <SelectValue placeholder="Campo" />
                        </SelectTrigger>
                        <SelectContent>
                          {customFields.map((field) => (
                            <SelectItem key={field.id} value={field.id}>
                              {field.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="align-top">
                      <Select
                        value={condition.operator}
                        onValueChange={(operator) =>
                          update(index, {
                            operator:
                              operator as QualificationCondition['operator'],
                            value: ['EXISTS', 'NOT_EXISTS'].includes(operator)
                              ? null
                              : condition.value,
                          })
                        }
                      >
                        <SelectTrigger className="min-w-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableOperators.map((operator) => (
                            <SelectItem
                              key={operator.value}
                              value={operator.value}
                            >
                              {operator.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="align-top">
                      {!needsValue ? (
                        <Input value="" disabled placeholder="Não se aplica" />
                      ) : fieldType === 'SELECT' && fieldOptions.length > 0 ? (
                        <Select
                          value={condition.value ?? undefined}
                          onValueChange={(value) => update(index, { value })}
                        >
                          <SelectTrigger className="min-w-0">
                            <SelectValue placeholder="Selecione uma opção" />
                          </SelectTrigger>
                          <SelectContent>
                            {fieldOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : fieldType === 'BOOLEAN' ? (
                        <Select
                          value={condition.value ?? undefined}
                          onValueChange={(value) => update(index, { value })}
                        >
                          <SelectTrigger className="min-w-0">
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">Sim</SelectItem>
                            <SelectItem value="false">Não</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          type={
                            fieldType === 'NUMBER'
                              ? 'number'
                              : fieldType === 'DATE'
                                ? 'date'
                                : 'text'
                          }
                          value={condition.value ?? ''}
                          placeholder="Valor esperado"
                          onChange={(event) =>
                            update(index, { value: event.target.value || null })
                          }
                        />
                      )}
                    </TableCell>
                    {showWeight && (
                      <TableCell className="align-top">
                        <Input
                          type="number"
                          min={-100}
                          max={100}
                          value={condition.weight}
                          aria-label="Pontos"
                          className="w-20 min-w-20"
                          onChange={(event) =>
                            update(index, {
                              weight: Number(event.target.value),
                            })
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell className="align-top">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() =>
                          onChange(
                            conditions.filter(
                              (_, currentIndex) => currentIndex !== index
                            )
                          )
                        }
                      >
                        <Trash2 className="text-destructive size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

export function QualificationTab({
  customFields,
  stages,
  policy,
  onPolicyChange,
}: QualificationTabProps) {
  const qualificationFields = [...SYSTEM_FIELDS, ...customFields]

  // Pré-preenche o "Funil por resultado" a partir dos estágios do kanban quando o
  // agente ainda não tem transições configuradas. Roda uma vez por montagem e
  // nunca sobrescreve o que já existe — o usuário pode ajustar depois.
  const seededFunnelRef = useRef(false)
  useEffect(() => {
    if (seededFunnelRef.current) return
    if (policy.funnelTransitions.length > 0) return
    if (stages.length === 0) return
    const suggested = suggestFunnelTransitions(stages)
    if (suggested.length === 0) return
    seededFunnelRef.current = true
    onPolicyChange({ ...policy, funnelTransitions: suggested })
  }, [stages, policy, onPolicyChange])

  function toggleField(fieldId: string) {
    const existing = policy.qualification.fields.find(
      (field) => field.fieldId === fieldId
    )
    const fields = existing
      ? policy.qualification.fields.filter((field) => field.fieldId !== fieldId)
      : [
          ...policy.qualification.fields,
          {
            fieldId,
            requirement: 'REQUIRED' as const,
            order: policy.qualification.fields.length,
            suggestedQuestion: null,
            allowInference: false,
            minConfidence: 1,
            requireConfirmation: false,
            allowCorrection: true,
            sensitive: false,
          },
        ]
    onPolicyChange({
      ...policy,
      qualification: { ...policy.qualification, fields },
    })
  }

  function updateField(
    fieldId: string,
    patch: Partial<AgentOperationalPolicy['qualification']['fields'][number]>
  ) {
    onPolicyChange({
      ...policy,
      qualification: {
        ...policy.qualification,
        fields: policy.qualification.fields.map((field) =>
          field.fieldId === fieldId ? { ...field, ...patch } : field
        ),
      },
    })
  }

  function setTransition(event: FunnelEvent, stageId: string) {
    const rest = policy.funnelTransitions.filter(
      (transition) => transition.event !== event
    )
    onPolicyChange({
      ...policy,
      funnelTransitions:
        stageId === 'none' ? rest : [...rest, { event, stageId }],
    })
  }

  const renderFieldRow = (field: CustomFieldDef) => {
    const configured = policy.qualification.fields.find(
      (item) => item.fieldId === field.id
    )
    return (
      <div
        key={field.id}
        className="border-border flex flex-col gap-3 border-b p-4 last:border-0"
      >
        <div className="flex items-center gap-3">
          <Checkbox
            checked={Boolean(configured)}
            onCheckedChange={() => toggleField(field.id)}
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{field.name}</p>
            <p className="text-muted-foreground font-mono text-xs">
              {field.key} · {field.fieldType}
            </p>
          </div>
          {configured && (
            <Select
              value={configured.requirement}
              onValueChange={(requirement) =>
                updateField(field.id, {
                  requirement: requirement as
                    | 'REQUIRED'
                    | 'OPTIONAL'
                    | 'CONDITIONAL',
                })
              }
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="REQUIRED">Obrigatório</SelectItem>
                <SelectItem value="OPTIONAL">Opcional</SelectItem>
                <SelectItem value="CONDITIONAL">Condicional</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        {configured && (
          <div className="flex flex-col gap-3 pl-7">
            <Input
              value={configured.suggestedQuestion ?? ''}
              placeholder="Pergunta sugerida para coletar este dado"
              onChange={(event) =>
                updateField(field.id, {
                  suggestedQuestion: event.target.value || null,
                })
              }
            />
            <p className="text-muted-foreground text-xs">
              {configured.requirement === 'REQUIRED'
                ? 'A qualificação não termina enquanto este dado estiver ausente.'
                : configured.requirement === 'OPTIONAL'
                  ? 'A IA pode coletar este dado, mas ele não bloqueia a qualificação.'
                  : 'O dado será solicitado quando as regras da operação indicarem necessidade.'}
            </p>
            <Collapsible>
              <CollapsibleTrigger className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs [&[data-state=open]>svg]:rotate-180">
                <ChevronDown className="size-3.5 transition-transform" />
                Opções avançadas
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-3 flex flex-col gap-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="flex items-center justify-between gap-2 text-xs">
                    <span>Inferir sem perguntar</span>
                    <Switch
                      checked={configured.allowInference}
                      onCheckedChange={(allowInference) =>
                        updateField(field.id, { allowInference })
                      }
                    />
                  </label>
                  <label className="flex items-center justify-between gap-2 text-xs">
                    <span>Pedir confirmação ao lead</span>
                    <Switch
                      checked={configured.requireConfirmation}
                      onCheckedChange={(requireConfirmation) =>
                        updateField(field.id, { requireConfirmation })
                      }
                    />
                  </label>
                  <label className="flex items-center justify-between gap-2 text-xs">
                    <span>Dado sensível</span>
                    <Switch
                      checked={configured.sensitive}
                      onCheckedChange={(sensitive) =>
                        updateField(field.id, { sensitive })
                      }
                    />
                  </label>
                  <label className="flex items-center justify-between gap-2 text-xs">
                    <span>Permitir correção posterior</span>
                    <Switch
                      checked={configured.allowCorrection}
                      onCheckedChange={(allowCorrection) =>
                        updateField(field.id, { allowCorrection })
                      }
                    />
                  </label>
                </div>
                {configured.allowInference && (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">
                        Confiança mínima para inferir
                      </span>
                      <span className="font-medium">
                        {Math.round(configured.minConfidence * 100)}%
                      </span>
                    </div>
                    <Slider
                      value={[configured.minConfidence]}
                      min={0}
                      max={1}
                      step={0.05}
                      onValueChange={(values) =>
                        updateField(field.id, {
                          minConfidence: values[0] ?? 0,
                        })
                      }
                    />
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-foreground text-sm font-semibold">
              Dados a coletar
            </h3>
            <p className="text-muted-foreground mt-0.5 text-xs">
              Marque quais informações a IA deve buscar durante a conversa e, se
              quiser, ajuste como cada dado é coletado. Isso não depende do
              prompt.
            </p>
          </div>
          <Link
            to="/organizacao/campos"
            className="text-primary flex items-center gap-1 text-xs hover:underline"
          >
            Gerenciar campos
            <ExternalLink className="size-3" />
          </Link>
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
              Dados do sistema
            </p>
            <div className="border-border bg-card overflow-hidden rounded-lg border">
              {SYSTEM_FIELDS.map(renderFieldRow)}
            </div>
          </div>
          <div>
            <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
              Campos personalizados
            </p>
            {customFields.length === 0 ? (
              <div className="border-border bg-muted/40 flex flex-col items-center gap-2 rounded-lg border border-dashed py-10 text-center">
                <Tag className="text-muted-foreground size-6" />
                <p className="text-sm font-medium">
                  Nenhum campo personalizado
                </p>
                <Link
                  to="/organizacao/campos"
                  className="text-primary text-xs hover:underline"
                >
                  Criar campos personalizados
                </Link>
              </div>
            ) : (
              <div className="border-border bg-card overflow-hidden rounded-lg border">
                {customFields.map(renderFieldRow)}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h3 className="text-foreground text-sm font-semibold">
              Regras de qualificação
            </h3>
            <p className="text-muted-foreground mt-0.5 text-xs">
              Condições atendidas somam pontos. Condições comuns não atendidas
              mantêm o lead em andamento; somente um desqualificador produz
              desqualificação imediata.
            </p>
          </div>
          <Select
            value={policy.qualification.groups[0]?.mode ?? 'ALL'}
            onValueChange={(mode) => {
              const current = policy.qualification.groups[0] ?? {
                id: 'commercial-fit',
                label: 'Fit comercial',
                mode: 'ALL' as const,
                conditions: [],
              }
              onPolicyChange({
                ...policy,
                qualification: {
                  ...policy.qualification,
                  groups: [
                    {
                      ...current,
                      mode: mode as 'ALL' | 'ANY',
                    },
                  ],
                },
              })
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas devem ser atendidas</SelectItem>
              <SelectItem value="ANY">
                Qualquer uma pode ser atendida
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <ConditionsEditor
          title="Critérios e pontuação"
          description="Cada condição verdadeira soma seu peso. O total é limitado entre 0 e 100."
          customFields={qualificationFields}
          conditions={policy.qualification.groups[0]?.conditions ?? []}
          showWeight
          onChange={(conditions) =>
            onPolicyChange({
              ...policy,
              qualification: {
                ...policy.qualification,
                groups: [
                  {
                    id: policy.qualification.groups[0]?.id ?? 'commercial-fit',
                    label:
                      policy.qualification.groups[0]?.label ?? 'Fit comercial',
                    mode: policy.qualification.groups[0]?.mode ?? 'ALL',
                    conditions,
                  },
                ],
              },
            })
          }
        />
        <ConditionsEditor
          title="Desqualificadores absolutos"
          description="Se qualquer condição abaixo for verdadeira, o lead será desqualificado imediatamente, independentemente do score."
          customFields={qualificationFields}
          conditions={policy.qualification.disqualifiers}
          showWeight={false}
          onChange={(disqualifiers) =>
            onPolicyChange({
              ...policy,
              qualification: {
                ...policy.qualification,
                disqualifiers,
              },
            })
          }
        />
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <div className="border-border bg-card rounded-lg border p-4">
          <p className="text-sm font-semibold">Pontuação mínima</p>
          <p className="text-muted-foreground mt-1 text-xs">
            Score mínimo para considerar o lead qualificado.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <Input
              type="number"
              min={0}
              max={100}
              value={policy.qualification.qualifiedScoreThreshold}
              onChange={(event) =>
                onPolicyChange({
                  ...policy,
                  qualification: {
                    ...policy.qualification,
                    qualifiedScoreThreshold: Number(event.target.value),
                  },
                })
              }
            />
            <Badge variant="secondary">0 a 100</Badge>
          </div>
        </div>
        <div className="border-border bg-card rounded-lg border p-4">
          <p className="text-sm font-semibold">Dados obrigatórios ausentes</p>
          <p className="text-muted-foreground mt-1 text-xs">
            Define o resultado enquanto ainda faltarem informações obrigatórias.
          </p>
          <Select
            value={policy.qualification.missingDataOutcome}
            onValueChange={(missingDataOutcome) =>
              onPolicyChange({
                ...policy,
                qualification: {
                  ...policy.qualification,
                  missingDataOutcome: missingDataOutcome as
                    | 'IN_PROGRESS'
                    | 'NEEDS_HUMAN',
                },
              })
            }
          >
            <SelectTrigger className="mt-4">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="IN_PROGRESS">
                Manter qualificação em andamento
              </SelectItem>
              <SelectItem value="NEEDS_HUMAN">
                Solicitar atendimento humano
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      <section className="border-border bg-card rounded-lg border p-4">
        <p className="text-sm font-semibold">Funil por resultado</p>
        <p className="text-muted-foreground mt-1 text-xs">
          Mova o lead no funil automaticamente conforme o resultado da
          qualificação. Sugerimos estágios pelo nome do seu funil — ajuste como
          preferir.
        </p>
        <div className="mt-4 grid gap-3">
          {FUNNEL_EVENTS.map((item, index) => {
            const showDivider =
              !item.primary && Boolean(FUNNEL_EVENTS[index - 1]?.primary)
            return (
              <div key={item.event}>
                {showDivider && (
                  <p className="text-muted-foreground border-border mb-3 border-t pt-3 text-xs font-medium">
                    Outras transições
                  </p>
                )}
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-[190px_1fr] sm:items-center sm:gap-3">
                  <Label
                    className="text-muted-foreground text-xs"
                    title={item.help}
                  >
                    {item.label}
                  </Label>
                  <Select
                    value={
                      policy.funnelTransitions.find(
                        (transition) => transition.event === item.event
                      )?.stageId ?? 'none'
                    }
                    onValueChange={(stageId) =>
                      setTransition(item.event, stageId)
                    }
                  >
                    <SelectTrigger className="min-w-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        Não mover automaticamente
                      </SelectItem>
                      {stages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>
                          {stage.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      <div className="border-primary/20 bg-primary/5 flex gap-3 rounded-lg border p-4">
        <Info className="text-primary mt-0.5 size-4 shrink-0" />
        <div>
          <p className="text-sm font-medium">Como o resultado é calculado</p>
          <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
            Primeiro o sistema verifica desqualificadores e dados obrigatórios.
            Depois valida o grupo de critérios e soma os pontos das condições
            atendidas. O lead só será qualificado quando não houver pendências,
            o grupo estiver atendido e o score alcançar o mínimo configurado.
          </p>
        </div>
      </div>
    </div>
  )
}
